<template>
  <b-overlay rounded="lg">
    <b-card title="Bulk Update Warranty" sub-title="Step 1 - Search & Download" class="mb-2 container-fluid">
      <b-col cols="12">
        <b-form @submit.prevent="onSearch">
          <div class="row">
            <div class="col-md-4">
              <b-form-group label="Installation Date From">
                <b-form-datepicker
                  v-model="filters.dateFrom"
                  placeholder="Select start date"
                  @input="onDateFromChange"
                />
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="Installation Date To">
                <b-form-datepicker
                  v-model="filters.dateTo"
                  placeholder="Select end date"
                  @input="onDateToChange"
                />
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="PCode">
                <b-form-select v-model="filters.pcode" :options="pcodeOptions" />
              </b-form-group>
            </div>
          </div>
          <div class="d-flex gap-2">
            <b-button type="submit" variant="primary" :disabled="loading">
              <b-spinner small v-if="loading"></b-spinner>
              {{ loading ? 'Searching...' : 'Search' }}
            </b-button>
            <b-button variant="success" class="ml-1" :disabled="results.length===0" @click="downloadExcel">Download to Excel</b-button>
            <b-button variant="info" class="ml-1" :disabled="!canProceed" @click="goToStep2">Continue to Bulk Update warranty</b-button>
            <b-button variant="secondary" class="ml-1" @click="$router.push({ name: 'bulk-update-warranty-history' })">View History</b-button>
          </div>
          <small class="text-muted d-block mt-2">Rules: Only proceed if all results have the same PCode. Date range cannot exceed 30 days.</small>
        </b-form>

        <b-alert v-if="results.length && !allSamePcode" show variant="warning" class="mb-2">
          All PCode must be the same to proceed.
        </b-alert>

        <b-alert v-if="dateRangeError" show variant="danger" class="mb-2">
          {{ dateRangeError }}
        </b-alert>

        <b-table v-if="searched" :items="results" :fields="fields" small responsive class="warranty-table">
          <template #cell(generalWarrantyEndDate)="{ item }">{{ formatDate(item.generalWarrantyEndDate) }}</template>
          <template #cell(installationDate)="{ item }">{{ formatDate(item.installationDate) }}</template>
          <template #cell(generalWarrantyDuration)="{ item }">{{ item.generalWarrantyDuration }} months</template>
        </b-table>
      </b-col>
    </b-card>
  </b-overlay>
</template>

<script>
import ExcelJS from 'exceljs'
import { mapActions } from 'vuex'
import { dateFormat } from '@/utils/utils'

export default {
  name: 'BulkUpdateWarrantyStep1',
  data() {
    return {
      filters: {
        dateFrom: '',
        dateTo: '',
        pcode: 'NR',
      },
      pcodeOptions: [
        { value: 'NR', text: 'NR' },
        { value: 'PRO', text: 'PRO' },
        { value: 'TCPR', text: 'TCPR' },
        { value: 'DL24', text: 'DL24' },
        { value: 'DS24', text: 'DS24' },
        { value: 'VP24', text: 'VP24' },
        { value: 'EPRO', text: 'EPRO' },
        { value: 'PPM2', text: 'PPM2' },
      ],
      searched: false,
      loading: false,
      dateRangeError: '',
      fields: [
        { key: 'certNo', label: 'Cert No'},
        { key: 'itemModel', label: 'Item Model'},
        { key: 'barcode', label: 'Barcode'},
        { key: 'serial', label: 'Serial'},
        { key: 'installationDate', label: 'Installation Date'},
        { key: 'generalWarrantyEndDate', label: 'General Warranty End Date'},
        { key: 'generalWarrantyDuration', label: 'General Warranty Duration (Month)'},
        { key: 'pcode', label: 'PCODE'},
      ],
    }
  },
  computed: {
    results() {
      var tempResults = this.$store.getters["warranty/getBulkUpdateResults"]
      return tempResults;
    },
    allSamePcode() {
      if (!this.results.length) return false
      const set = new Set(this.results.map(r => r.pcode))
      return set.size === 1
    },
    canProceed() {
      return this.results.length > 0 && this.allSamePcode && !this.dateRangeError
    },
    isValidDateRange() {
      if (!this.filters.dateFrom || !this.filters.dateTo) return true
      const fromDate = new Date(this.filters.dateFrom)
      const toDate = new Date(this.filters.dateTo)
      const diffTime = Math.abs(toDate - fromDate)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return diffDays <= 30
    },
  },
  methods: {
    ...mapActions({
      searchBulkUpdateWarranty: "warranty/searchBulkUpdateWarranty"
    }),
    formatDate(d) {
      return d ? dateFormat(d) : ''
    },
    validateDateRange() {
      this.dateRangeError = ''
      
      if (!this.filters.dateFrom || !this.filters.dateTo) return
      
      const fromDate = new Date(this.filters.dateFrom)
      const toDate = new Date(this.filters.dateTo)
      const diffTime = Math.abs(toDate - fromDate)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      if (diffDays > 30) {
        this.dateRangeError = 'Date range cannot exceed 30 days. Please adjust your date selection.'
      }
    },
    onDateFromChange(newDate) {
      if (!newDate) {
        this.dateRangeError = ''
        return
      }
      
      const fromDate = new Date(newDate)
      
      // If dateTo exists and dateFrom is changed to be earlier, adjust dateTo to be 30 days from dateFrom
      if (this.filters.dateTo) {
        const currentToDate = new Date(this.filters.dateTo)
        
        // If dateFrom is earlier than the current range or if range exceeds 30 days, adjust dateTo
        if (fromDate < currentToDate) {
          const diffTime = Math.abs(currentToDate - fromDate)
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
          
          if (diffDays > 30) {
            // Set dateTo to be exactly 30 days from dateFrom
            const newToDate = new Date(fromDate)
            newToDate.setDate(newToDate.getDate() + 30)
            this.filters.dateTo = newToDate.toISOString().split('T')[0]
          }
        }
      }
      
      this.$nextTick(() => {
        this.validateDateRange()
      })
    },
    onDateToChange(newDate) {
      this.$nextTick(() => {
        this.validateDateRange()
      })
    },
    async onSearch() {
      // Validate date range before searching
      this.validateDateRange()
      if (this.dateRangeError) {
        this.$bvToast.toast(this.dateRangeError, { title: 'Invalid Date Range', variant: 'danger', solid: true })
        return
      }
      
      this.searched = true
      this.loading = true
      
      try {
        await this.searchBulkUpdateWarranty(this.filters)
      } catch (err) {
        console.error('Failed to fetch warranties', err)
        this.$bvToast.toast('Failed to search warranties', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.loading = false
      }
    },
    async downloadExcel() {
      const wb = new ExcelJS.Workbook()
      const ws = wb.addWorksheet('Results')

      ws.columns = [
        { header: 'Cert No', key: 'certNo', width: 30 },
        { header: 'Item Model', key: 'itemModel', width: 25 },
        { header: 'Barcode', key: 'barcode', width: 20 },
        { header: 'Serial', key: 'serial', width: 20 },
        { header: 'Installation Date', key: 'installationDate', width: 18 },
        { header: 'General Warranty End Date', key: 'generalWarrantyEndDate', width: 30 },
        { header: 'General Warranty Duration (Month)', key: 'generalWarrantyDuration', width: 35 },
        { header: 'PCODE', key: 'pcode', width: 12 },
      ]

      ws.getRow(1).font = { bold: true }

      // push rows
      this.results.forEach(r => {
        ws.addRow({
          barcode: r.barcode,
          serial: r.serial,
          certNo: r.certNo,
          itemModel: r.itemModel,
          installationDate: r.installationDate ? new Date(r.installationDate) : null,
          generalWarrantyEndDate: r.generalWarrantyEndDate ? new Date(r.generalWarrantyEndDate) : null,
          generalWarrantyDuration: r.generalWarrantyDuration,
          pcode: r.pcode,
        })
      })

      // set date format
      ws.getColumn('installationDate').numFmt = 'yyyy-mm-dd'
      ws.getColumn('generalWarrantyEndDate').numFmt = 'yyyy-mm-dd'

      const buf = await wb.xlsx.writeBuffer()
      const blob = new Blob([buf], { type: 'application/octet-stream' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'bulk_warranty_results.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    },
    goToStep2() {
      if (!this.canProceed) return
      this.$router.push({ name: 'bulk-update-warranty-step2' })
    },
  },
  created() {
    document.title = "Bulk Update Warranty - Step 1 | RSP";
  },
  mounted() {
    // Load saved filters if any
    const savedFilters = this.$store.getters["warranty/getBulkUpdateFilters"];
    if (savedFilters && Object.keys(savedFilters).length > 0) {
      this.filters = { ...savedFilters };
    }
  },
}
</script>

<style scoped>
</style>